import {
  AXM_DENOM,
  AXM_DECIMALS,
  msgExecuteContract,
  toMicro,
  type CosmosMsg,
} from "./types.js";

/** Lock presets in seconds */
export const LOCK_OPTIONS = [
  { days: 30, seconds: 30 * 24 * 3600, label: "30 дней" },
  { days: 90, seconds: 90 * 24 * 3600, label: "90 дней" },
  { days: 180, seconds: 180 * 24 * 3600, label: "180 дней" },
  { days: 365, seconds: 365 * 24 * 3600, label: "365 дней" },
] as const;

/** Minimum counter-token deposit value in AXM (human) */
export const MIN_SUPPLY_AXM = 500_000;

/**
 * Economics (on-chain):
 * - AXM LPs: 90% of pool fees, 80% of LP Bonus
 * - Platform cut: 10% pool + 20% LP Bonus
 * - Token suppliers: 40% of platform cut → 4% pool + 8% LP Bonus
 * - Treasury: 60% of platform cut → 6% pool + 12% LP Bonus
 */
export const ECONOMICS = {
  userPoolShare: 0.9,
  userLpBonusShare: 0.8,
  platformPoolCut: 0.1,
  platformLpBonusCut: 0.2,
  supplierOfPlatform: 0.4,
  supplierPoolEffective: 0.04,
  supplierLpBonusEffective: 0.08,
  treasuryPoolEffective: 0.06,
  treasuryLpBonusEffective: 0.12,
  minSupplyAxm: MIN_SUPPLY_AXM,
} as const;

/** CW20 Send → vault SupplyToken hook */
export function buildSupplyTokenMsg(params: {
  sender: string;
  tokenContract: string;
  vault: string;
  symbol: string;
  amountHuman: string;
  decimals?: number;
  pool?: string;
  lockSeconds: number;
}): CosmosMsg {
  const amount = toMicro(params.amountHuman, params.decimals ?? AXM_DECIMALS);
  const hook = {
    supply_token: {
      symbol: params.symbol,
      pool: params.pool ?? null,
      lock_seconds: params.lockSeconds,
    },
  };
  const msgB64 =
    typeof btoa === "function"
      ? btoa(JSON.stringify(hook))
      : Buffer.from(JSON.stringify(hook), "utf8").toString("base64");

  return msgExecuteContract({
    sender: params.sender,
    contract: params.tokenContract,
    msg: {
      send: {
        contract: params.vault,
        amount,
        msg: msgB64,
      },
    },
  });
}

/** Harvest only when explicitly requested — auto-harvest caused wallet "Load Failed". */
export function buildSupplyTokenMsgs(params: {
  sender: string;
  tokenContract: string;
  vault: string;
  symbol: string;
  amountHuman: string;
  decimals?: number;
  pool?: string;
  lockSeconds: number;
  withHarvest?: boolean;
}): CosmosMsg[] {
  const send = buildSupplyTokenMsg(params);
  if (params.withHarvest) {
    return [
      msgExecuteContract({
        sender: params.sender,
        contract: params.vault,
        msg: { harvest: { symbol: params.symbol } },
      }),
      send,
    ];
  }
  return [send];
}

export function buildWithdrawSupplierMsg(params: {
  sender: string;
  vault: string;
  lotId: number;
  amountHuman?: string;
  decimals?: number;
}): CosmosMsg {
  return msgExecuteContract({
    sender: params.sender,
    contract: params.vault,
    msg: {
      withdraw_supplier_inventory: {
        lot_id: params.lotId,
        amount: params.amountHuman
          ? toMicro(params.amountHuman, params.decimals ?? AXM_DECIMALS)
          : null,
      },
    },
  });
}

export function buildClaimSupplierMsg(params: {
  sender: string;
  vault: string;
  lotId?: number;
}): CosmosMsg {
  return msgExecuteContract({
    sender: params.sender,
    contract: params.vault,
    msg: {
      claim_supplier_rewards: {
        lot_id: params.lotId ?? null,
      },
    },
  });
}

export function estimateTokenAmountForMinAxm(
  minAxmHuman: number,
  priceTokenInAxm: number,
): number {
  if (!priceTokenInAxm || priceTokenInAxm <= 0) return 0;
  return minAxmHuman / priceTokenInAxm;
}

export { AXM_DENOM };
